import classesText from './ccf-app-agent-messages.module.scss';

import { ConversationsAppSideBar } from './conversations-app-sidebar/conversations-app-sidebar';
import { ConversationsAppContainer } from './conversations-app-container/conversations-app-container';
import { ConversationsReplyAppContainer } from './conversations-reply-app-container/conversations-reply-app-container';
import { Paper, Divider, Theme } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { CONVERSATION_SEARCH_HEIGHT } from './utils/conversations.utils';
import { ConversationAppSearch } from './conversations-app-search/conversations-app-search';
import {
  CcfBox,
  CcfConversationIcon,
  CcfEditWithPenIcon,
  CcfHeader,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  ccfConversationsActions,
  getAgentMessagesAppSpaceView,
  getAgents,
  getIsNewMessage,
  getSelectedAgent,
} from './+state/ccf-conversations.slice';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { agentsData } from './mock-data/agent-conversations-mock-data';
import {
  getAppSpaceRatio,
  getConversationMessage,
} from '../../global.app.slice';
import { activeContactSelector } from '../../ccf-inbox/ccf-inbox.slice';
import { MediaType } from '@nice-ccf/core-sdk';
import ConversationsNewMessage from './conversations-new-message/conversations-new-message';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      margin: 'auto',
      height: '100%',
      boxShadow: 'none',
    },
    textField: {
      zIndex: theme.zIndex.drawer + 1,
      background: theme.palette.background.paper,
      '& > .MuiInputBase-root': {
        height: 32,
        fontSize: 'small',
      },
    },
    header: {
      padding: '8px 16px',
      background: theme.palette.background.level1,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    displayFlex: {
      display: 'flex',
    },
    flexDirectionColumn: {
      flexDirection: 'column',
    },
    contentContainer: {
      width: '100%',
      borderTop: '1px Solid #e3e9ee',
      height: `calc( 100% - ${CONVERSATION_SEARCH_HEIGHT}px )`,
    },
    conversationContent: {
      height: '100%',
      width: '100%',
      padding: '0px 10px',
    },
    headerText: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '12px',
      lineHeight: '16px',
      color: '#3f5c69',
    },
    leftSideBar: {
      width: '30%',
      borderRight: '1px solid #e3e9ee',
    },
  })
);

/* eslint-disable-next-line */
export interface CcfAppAgentMessagesProps {
  isAppSpace?: boolean;
}

/**
 * Component for Conversations feature
 * @param props - CcfAppAgentMessagesProps
 * @example <CcfAppAgentMessages />
 * @returns
 */
export function CcfAppAgentMessages(props: CcfAppAgentMessagesProps) {
  const [translate] = useTranslator();
  const styles = useStyles();
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  const {
    updateSelectedAgent,
    updateAgentGroups,
    updateAgents,
    setAgentMessagesAppSpaceView,
    setIsNewMessage,
    setNewAgentSelected,
  } = ccfConversationsActions;
  const appSpaceRatio = useSelector(getAppSpaceRatio);
  const agentMessagesAppSpaceView = useSelector(getAgentMessagesAppSpaceView);
  const isNewMessage = useSelector(getIsNewMessage);
  const selectedAgent = useSelector(getSelectedAgent);
  const selectedAgentRef = useRef(selectedAgent);
  useEffect(() => {
    selectedAgentRef.current = selectedAgent;
  }, [selectedAgent]);
  const agents = useSelector(getAgents);
  const conversationMessage = useSelector(getConversationMessage);

  const initialAgent = useMemo(() => agentsData[0], []);

  const agentsRef = useRef(agents);
  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  // One-time initialization: reset mock data and pick the default agent.
  // Kept separate from the conversationMessage effect so that re-selecting an
  // agent (which updates the `agents` array) doesn't snap the selection back.
  useEffect(() => {
    dispatch(updateAgents({ type: 'reset' }));
    dispatch(updateAgentGroups({ type: 'reset' }));
    dispatch(updateSelectedAgent({ agent: initialAgent, type: 'favourite' }));
    dispatch(setIsNewMessage(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Append incoming conversationMessage to the Supervisor thread.
  useEffect(() => {
    if (!conversationMessage) return;
    const supervisorAgent = agentsRef.current?.find(
      (a: any) => a.name === 'Supervisor'
    );
    if (!supervisorAgent) return;
    const alreadyHasMessage = (supervisorAgent.messages || []).some(
      (m: any) =>
        m.text === conversationMessage.text && m.name === 'another agent'
    );
    if (alreadyHasMessage) return;
    const newMessage = {
      name: 'another agent',
      text: conversationMessage.text,
      status: conversationMessage.status || 'available',
      timestamp: conversationMessage.timestamp || new Date().toISOString(),
      color: '#FBC231',
    };
    const updatedSupervisor = {
      ...supervisorAgent,
      messages: [...(supervisorAgent.messages || []), newMessage],
    };
    dispatch(updateAgents({ agent: updatedSupervisor, type: 'update' }));
    if (selectedAgentRef.current?.name === 'Supervisor') {
      dispatch(
        updateSelectedAgent({ agent: updatedSupervisor, type: 'direct' })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationMessage]);

  useEffect(() => {
    if (appSpaceRatio && appSpaceRatio <= 40) {
      dispatch(setAgentMessagesAppSpaceView('agentsView'));
    } else {
      dispatch(setAgentMessagesAppSpaceView('combinedView'));
    }
  }, [appSpaceRatio]);

  useEffect(() => {
    if (activeContact?.media === MediaType.VOICE) {
      dispatch(setAgentMessagesAppSpaceView('combinedView'));
    }
  }, [activeContact]);

  const leftSideBarStyle = useMemo(
    () => ({
      width:
        props?.isAppSpace && agentMessagesAppSpaceView === 'combinedView'
          ? '40%'
          : props?.isAppSpace && agentMessagesAppSpaceView === 'agentsView'
          ? '100%'
          : '30%',
      display:
        (props?.isAppSpace &&
          (agentMessagesAppSpaceView === 'combinedView' ||
            agentMessagesAppSpaceView === 'agentsView')) ||
        !props?.isAppSpace
          ? 'block'
          : 'none',
    }),
    [props?.isAppSpace, agentMessagesAppSpaceView]
  );

  const rightSideBarStyle = useMemo(
    () => ({
      width:
        props?.isAppSpace && agentMessagesAppSpaceView === 'combinedView'
          ? '60%'
          : props?.isAppSpace && agentMessagesAppSpaceView === 'messagesView'
          ? '100%'
          : '70%',
      display:
        (props?.isAppSpace &&
          (agentMessagesAppSpaceView === 'combinedView' ||
            agentMessagesAppSpaceView === 'messagesView')) ||
        !props?.isAppSpace
          ? 'block'
          : 'none',
    }),
    [props?.isAppSpace, agentMessagesAppSpaceView]
  );

  /**
   * Method to handle new message
   * @example newMessageHandler()
   */
  const newMessageHandler = () => {
    dispatch(setIsNewMessage(true)); //when new message is clicked, show new message view
    dispatch(updateSelectedAgent({ agent: {}, type: '' })); //when new message is clicked, reset the selected agent
    dispatch(setNewAgentSelected({})); //when new message is clicked, reset the new agent selected
    if (appSpaceRatio && appSpaceRatio <= 40) {
      dispatch(setAgentMessagesAppSpaceView('messagesView'));
    }
  };

  return (
    <Paper className={styles.root} square>
      <CcfBox className={classesText.headerContainer}>
        <CcfHeader
          headerText={translate('agentMessages')}
          LeftIcon={
            <CcfConversationIcon
              style={{ color: '#007AB8', marginTop: '6px' }}
              fontSize="small"
            />
          }
          rightContent={<ConversationAppSearch showSearchBox={true} />}
          RightIcon={false}
          editWithPenIcon
          headerTextClassess={classesText.headerText}
        />
      </CcfBox>

      <section className={`${styles.displayFlex} ${styles.contentContainer}`}>
        <CcfBox
          style={{ width: '35%', display: 'block' }}
          className={styles.leftSideBar}
        >
          <CcfBox className={classesText.newMessageContainer}>
            <CcfTooltip title={translate('newMessage')}>
              <CcfEditWithPenIcon
                style={{ width: '16px' }}
                className="pointerEvent"
                onClick={newMessageHandler}
              />
            </CcfTooltip>
          </CcfBox>
          <ConversationsAppSideBar />
        </CcfBox>

        <CcfBox
          style={rightSideBarStyle}
          className={`${styles.displayFlex} ${styles.flexDirectionColumn} ${styles.conversationContent}`}
        >
          {isNewMessage ? (
            <ConversationsNewMessage />
          ) : (
            <ConversationsAppContainer />
          )}

          <Divider style={{ marginBottom: '10px' }} />
          <ConversationsReplyAppContainer />
        </CcfBox>
      </section>
    </Paper>
  );
}

export default CcfAppAgentMessages;
